'use client';

import React, { useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useLenis } from '@hooks/useLenis';

interface SmoothScrollLinkProps {
  href: string;
  children: React.ReactNode;
  offset?: number;
  className?: string;
  style?: React.CSSProperties;
  transition?: boolean;
}

export default function SmoothScrollLink({
  href,
  children,
  offset = 0,
  className,
  style,
  transition = false,
}: SmoothScrollLinkProps) {
  const { scrollTo } = useLenis();
  const router = useRouter();

  const hashIndex = href.indexOf('#');
  const path = hashIndex !== -1 ? href.slice(0, hashIndex) : href;
  const hash = hashIndex !== -1 ? href.slice(hashIndex) : '';

  const getFullHref = (p: string) => {
    if (!p.startsWith('/')) p = `/${p}`;
    return p;
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const locationHash = window.location.hash;
      if (locationHash) {
        const element = document.querySelector(locationHash);
        if (element) {
          const timer = setTimeout(() => {
            scrollTo(locationHash, { offset });
          }, 100);
          return () => clearTimeout(timer);
        }
      }
    }
  }, [offset, scrollTo]);

  const handleClick = (e: React.MouseEvent) => {
    if (hash && typeof document !== 'undefined') {
      const element = document.querySelector(hash);
      if (element) {
        e.preventDefault();
        scrollTo(hash, { offset });
        return;
      }
    }

    if (transition || hash) {
      e.preventDefault();
      const fullPath = getFullHref(path) + hash;
      router.push(fullPath);
    }
  };

  const fullHref = getFullHref(path) + hash;

  if (hash) {
    return (
      <a
        href={fullHref}
        className={className}
        style={{
          cursor: 'pointer',
          textDecoration: 'none',
          display: 'flex',
          ...style,
        }}
        onClick={handleClick}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      href={fullHref}
      className={className}
      style={{
        cursor: 'pointer',
        textDecoration: 'none',
        display: 'flex',
        ...style,
      }}
      onClick={handleClick}
    >
      {children}
    </Link>
  );
}
